// src/services/api/emailService.ts

import { useAuth } from "../../contexts/AuthContext";
import { awsConfig } from "../config/aws-config";
import { ImapEmail } from "../../types/imapEmail";

/**
 * Extended interface for the new send-email payload.
 * This matches what the new backend /emails/send endpoint expects.
 */
export interface SendEmailPayload {
  fromEmail: string; // e.g. "admin@hbinternational.org" or "support@..."
  recipient: string; // "to" address
  subject: string;
  message: string;

  // If you want to supply your own raw HTML instead of generalEmailTemplate
  htmlMessage?: string;

  // Additional optional fields for robust template usage
  recipientName?: string;
  ctaUrl?: string;
  ctaLabel?: string;
  signature?: string;

  // Basic optional fields
  cc?: string[];
  bcc?: string[];
  replyTo?: string[];
  scheduledTime?: string;

  // Attachments in base64 form
  attachments?: Array<{
    filename: string;
    base64Data: string;
    contentType?: string;
  }>;
}

/**
 * Helper to log emails in smaller chunks and summarize them by date for debugging.
 * This is optional. You can remove it if you don't need console logging.
 */
// function logCleanEmailStructure(emails: ImapEmail[]): void {
//   // const cleanEmails = emails.map((email) => {
//   //   const { body, html, textPreview, htmlLocation, ...cleanEmail } = email;
//   //   return {
//   //     ...cleanEmail,
//   //     bodySize: body?.length || 0,
//   //     htmlSize: email.htmlSize || (html?.length || 0),
//   //     textPreviewSize: textPreview?.length || 0,
//   //     hasHtmlLocation: !!htmlLocation,
//   //   };
//   // });

//   // const CHUNK_SIZE = 5;
//   // for (let i = 0; i < cleanEmails.length; i += CHUNK_SIZE) {
//   //   const chunk = cleanEmails.slice(i, i + CHUNK_SIZE);
//   //  ////console.log(
//   //   //   `Emails ${i + 1} to ${Math.min(i + CHUNK_SIZE, cleanEmails.length)}:`,
//   //   //   JSON.stringify(chunk, null, 2)
//   //   // );
//   // }

//   // Summarize by date
//   // const dateGroups = cleanEmails.reduce((acc, email) => {
//   //   const d = new Date(email.date);
//   //   let dateStr: string;
//   //   if (isNaN(d.getTime())) {
//   //     dateStr = "Invalid Date";
//   //   } else {
//   //     dateStr = d.toISOString().substring(0, 10);
//   //   }
//   //   if (!acc[dateStr]) {
//   //     acc[dateStr] = 0;
//   //   }
//   //   acc[dateStr]++;
//   //   return acc;
//   // }, {} as Record<string, number>);

//  ////console.log("Emails by date:", dateGroups);
// }

/**
 * A custom React hook that returns email-related methods,
 * automatically deriving tokens from AuthContext for Authorization.
 */
export function useEmailService() {
  const { tokens } = useAuth();

  /**
   * Returns a properly formatted Authorization header
   * using the ID token or Access token from Cognito.
   */
  const getAuthHeader = (): string => {
    if (!tokens?.idToken && !tokens?.accessToken) {
      throw new Error("No valid token found. User is likely not authenticated.");
    }
    const rawToken = tokens.idToken || tokens.accessToken;
    return rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
  };

  /**
   * Send a new email to the /emails/send endpoint.
   * The payload should match the new backend structure (fromEmail, recipient, attachments, etc.).
   */
  const sendEmail = async (payload: SendEmailPayload): Promise<any> => {
    const url = `${awsConfig.apiEndpoint}/emails/send`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send email: ${response.status}`);
    }

    return response.json();
  };

  /**
   * Fetch all emails (folder-agnostic).
   * @param refresh Optional parameter to trigger a background refresh of emails
   */
  const fetchImapEmails = async (refresh = false): Promise<ImapEmail[]> => {
    // If refresh is requested, first trigger a fetch operation
    if (refresh) {
      await triggerEmailFetch();
    }

    const url = `${awsConfig.apiEndpoint}/emails${refresh ? '?refresh=true' : ''}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch emails: ${response.status}`);
    }

    const data = await response.json();
    const emails = data.emails || [];
    // logCleanEmailStructure(emails);
    return emails;
  };

  /**
   * Explicitly trigger the email fetch process.
   * This makes a POST request to /emails/fetch to initiate the IMAP email fetching process
   */
  const triggerEmailFetch = async (): Promise<void> => {
    const url = `${awsConfig.apiEndpoint}/emails/fetch`;
    
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getAuthHeader(),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to trigger email fetch: ${response.status}`);
      }
      
    } catch (error) {
      // Don't throw here - we still want to continue with fetching existing emails
    }
  };

  /**
   * Fetch emails by folder (inbox, sent, drafts, etc.).
   */
  const fetchEmailsByFolder = async (folder: string): Promise<ImapEmail[]> => {
    const url = `${awsConfig.apiEndpoint}/emails?folder=${encodeURIComponent(folder)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Fetch folder error (${response.status}):`, errorText);
      throw new Error(`Failed to fetch emails from folder: ${folder}`);
    }

    const data = await response.json();
    return data.emails || [];
  };

  /**
   * Update an existing email (mark read, etc.).
   */
  const updateEmail = async (
    id: string,
    updatedFields: Partial<ImapEmail>
  ): Promise<{ success: boolean; email?: ImapEmail }> => {
    const url = `${awsConfig.apiEndpoint}/emails/${id}`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify(updatedFields),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Update email API error (${response.status}):`, errorText);
      throw new Error(`Failed to update email: ${response.status}`);
    }

    return response.json();
  };

  /**
   * Delete email permanently by ID.
   */
  const deleteEmailPermanently = async (id: string): Promise<{ success: boolean }> => {
    const url = `${awsConfig.apiEndpoint}/emails/${id}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Delete email API error (${response.status}):`, errorText);
      throw new Error(`Failed to delete email: ${response.status}`);
    }

    return { success: true };
  };

  /**
   * Move an email to trash (soft delete).
   */
  const moveEmailToTrash = async (emailId: string): Promise<void> => {
    const url = `${awsConfig.apiEndpoint}/emails/${emailId}/trash`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify({ deleted: true }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Move to trash API error (${response.status}):`, errorText);
      throw new Error("Failed to move email to trash");
    }
  };

  /**
   * Mark an email as read.
   */
  const markEmailAsRead = async (emailId: string): Promise<void> => {
    const url = `${awsConfig.apiEndpoint}/emails/${emailId}/read`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify({ read: true }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Mark as read API error (${response.status}):`, errorText);
      throw new Error("Failed to mark email as read");
    }
  };

  /**
   * Mark an email as starred.
   */
  const markEmailAsStarred = async (emailId: string): Promise<void> => {
    const url = `${awsConfig.apiEndpoint}/emails/${emailId}/star`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify({ starred: true }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Mark as starred API error (${response.status}):`, errorText);
      throw new Error("Failed to mark email as starred");
    }
  };

  /**
   * Restore an email from trash back to the inbox.
   */
  const restoreEmail = async (id: string): Promise<{ success: boolean; email?: ImapEmail }> => {
    const url = `${awsConfig.apiEndpoint}/emails/${id}/restore`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Restore email API error (${response.status}):`, errorText);
      throw new Error(`Failed to restore email: ${response.status}`);
    }

    return response.json();
  };

  /**
   * Download an email attachment by emailId & filename.
   */
  const downloadEmailAttachment = async (emailId: string, filename: string): Promise<Blob> => {
    const url = `${awsConfig.apiEndpoint}/emails/${emailId}/attachments/${encodeURIComponent(filename)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: getAuthHeader(),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Download attachment API error (${response.status}):`, errorText);
      throw new Error("Failed to download attachment");
    }

    return response.blob();
  };

  return {
    sendEmail,              // Our new method that posts to /emails/send
    fetchImapEmails,
    fetchEmailsByFolder,
    updateEmail,
    deleteEmailPermanently,
    moveEmailToTrash,
    markEmailAsRead,
    markEmailAsStarred,
    restoreEmail,
    downloadEmailAttachment,
    triggerEmailFetch,      // Added function to directly trigger email fetching
  };
}
